import { AttachmentFile, Message } from '@nextforce/api'
import { Divider, makeStyles, Text } from '@rneui/themed'
import { t } from 'app/i18l'
import { useMembers } from 'chat/members/queries/list'
import { convertNativeAttachmentToFile } from 'chat/shared/util'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ActivityIndicator,
  ImageChangeEventData,
  Platform,
  StyleSheet,
  TextInput,
  useWindowDimensions,
  View,
} from 'react-native'
import {
  MentionInput,
  MentionSuggestionsProps,
  Suggestion,
} from 'react-native-controlled-mentions'
import { FlatList, TouchableOpacity } from 'react-native-gesture-handler'
import {
  IMessage as ChatMessage,
  ComposerProps,
  InputToolbar,
  InputToolbarProps,
  Send,
  SendProps,
} from 'react-native-gifted-chat'
import Animated, { FadeInDown, FadeOutDown } from 'react-native-reanimated'
import {
  ATTACHMENT_PREVIEW_SIZE,
  AttachmentPreviewRow,
} from 'shared/components/chat/attachment_preview'
import CustomTextInput from 'shared/components/chat/custom_text_input.web'
import { UserAvatar } from 'shared/components/user/user_avatar'
import { WEB_SCREEN_WIDTH } from 'shared/constants'
import { useUser } from 'users/queries/detail'
import { REPLY_MESSAGE_BAR_HEIGHT, ReplyMessage } from './reply_message_bar'

export const COMPOSER_HEIGHT = 15
export type MentionSuggestionItem = Suggestion & {
  photoUrl: string | undefined
}

export const CustomComposer = ({
  props,
  messageBody,
  selectedAttachments,
  setChatMessageBody,
  chatGroupId,
  pasteAttachments,
  disabled,
  replyMessage,
  setReplyMessage,
  shouldFocusInput,
  setShouldFocusInput,
}: {
  props: ComposerProps
  chatGroupId: string
  messageBody: string
  selectedAttachments: AttachmentFile[]
  setChatMessageBody: (text: string) => void
  pasteAttachments: (chatGroupId: string, files: AttachmentFile[]) => void
  disabled: boolean
  replyMessage?: Message
  setReplyMessage: React.Dispatch<React.SetStateAction<Message | undefined>>
  shouldFocusInput: boolean
  setShouldFocusInput: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const styles = useStyles(selectedAttachments.length > 0)
  const dimensions = useWindowDimensions()
  const inputRef = useRef<TextInput>(null)
  const membersQuery = useMembers(chatGroupId)
  const userQuery = useUser()
  const mentionSuggestions = useMemo<MentionSuggestionItem[]>(() => {
    if (!membersQuery.data) {
      return []
    }

    return membersQuery.data
      .filter(member => member.id !== userQuery.data?.id)
      .map(member => ({
        id: member.id,
        name: `${member.firstName || ''} ${member.lastName || ''}`.trim(),
        photoUrl: member.photoUrl,
      }))
  }, [membersQuery.data, userQuery.data?.id])
  const shouldShowMentionLoading =
    !membersQuery.data && (membersQuery.isLoading || membersQuery.isFetching)
  const placeholder =
    disabled && !messageBody
      ? 'Uploading...'
      : props.textInputProps?.placeholder

  const handleAttachmentPaste = useCallback(
    async (data: ImageChangeEventData) => {
      const attachmentFile = await convertNativeAttachmentToFile({
        data,
      })
      if (attachmentFile) {
        pasteAttachments(chatGroupId, [attachmentFile])
      }
    },
    [chatGroupId, pasteAttachments]
  )

  useEffect(() => {
    if (shouldFocusInput) {
      inputRef.current?.focus()
      // Reset the focus trigger after attempting to focus
      setShouldFocusInput(false)
    }
  }, [shouldFocusInput, setShouldFocusInput])

  return (
    <>
      {replyMessage ? (
        <View
          style={[
            styles.replyMessageBar,
            {
              width:
                Platform.OS === 'web' ? WEB_SCREEN_WIDTH : dimensions.width,
            },
          ]}
        >
          <ReplyMessage
            setReplyMessage={setReplyMessage}
            replyMessage={replyMessage}
          />
          <Divider color={styles.divider.color} />
        </View>
      ) : null}
      {selectedAttachments.length > 0 ? (
        <View
          style={[
            styles.attachmentPreviewRowContainer,
            { width: dimensions.width },
          ]}
        >
          <AttachmentPreviewRow hideTitle chatGroupId={chatGroupId} />
          <Divider color={styles.divider.color} />
        </View>
      ) : null}
      {Platform.OS === 'web' ? (
        <CustomTextInput
          props={props}
          chatGroupId={chatGroupId}
          onTextChanged={setChatMessageBody}
          pasteAttachments={pasteAttachments}
          text={messageBody}
          disabled={disabled}
          placeholder={placeholder}
        />
      ) : (
        <MentionInput
          inputRef={inputRef}
          accessible
          accessibilityLabel={props.textInputProps?.placeholder}
          placeholder={props.textInputProps?.placeholder}
          placeholderTextColor={styles.placeholderTextColor.color}
          editable={props.textInputProps?.editable}
          multiline
          onContentSizeChange={e => {
            if (props.textInputProps?.onContentSizeChange) {
              props.textInputProps.onContentSizeChange(e)
            }
          }}
          onChange={text => {
            setChatMessageBody(text)
            props.textInputProps?.onChangeText?.(text)
          }}
          containerStyle={{
            ...styles.textInputContainer,
            ...Platform.select({
              ios: { minHeight: props.composerHeight },
              android: {
                height: props.composerHeight,
              },
            }),
          }}
          style={[
            props.textInputProps?.style,
            styles.textInput,
            disabled && { color: 'grey' },
          ]}
          autoFocus={props.textInputProps?.autoFocus}
          value={props.text ?? ''}
          underlineColorAndroid={'transparent'}
          keyboardAppearance={props.textInputProps?.keyboardAppearance}
          partTypes={[
            {
              trigger: '@',
              renderSuggestions: p =>
                renderSuggestions(
                  mentionSuggestions,
                  shouldShowMentionLoading
                )(p),
              textStyle: styles.mentionText,
              isInsertSpaceAfterMention: true,
            },
          ]}
          onImageChange={event => handleAttachmentPaste(event.nativeEvent)}
        />
      )}
    </>
  )
}

export const CustomSendButton = ({
  props,
  disabled,
  shouldShowSend,
}: {
  props: SendProps<ChatMessage>
  shouldShowSend: boolean
  disabled: boolean
}) => {
  const styles = useStyles()
  return (
    <Send
      {...props}
      sendButtonProps={{ enabled: !props.sendButtonProps?.enabled || disabled }}
      textStyle={[
        props.textStyle,
        { fontSize: 16 },
        disabled && { color: 'grey' },
      ]}
      text={
        shouldShowSend
          ? 'This field needs to be non-empty for send button to work'
          : ''
      }
      containerStyle={styles.sendContainer}
    />
  )
}

export const CustomInputToolbar = ({
  props,
}: {
  props: InputToolbarProps<ChatMessage>
}) => {
  const styles = useStyles()
  return (
    <InputToolbar
      {...props}
      primaryStyle={[props.primaryStyle, styles.inputToolbar]}
      containerStyle={styles.inputToolbarContainer}
    />
  )
}

const renderSuggestions =
  (
    suggestions: { id: string; name: string; photoUrl: string | undefined }[],
    shouldShowLoading: boolean
  ) =>
  ({ keyword, onSuggestionPress }: MentionSuggestionsProps) => {
    const styles = useStyles()
    //Throttling keyword changes to fix flickering
    const [calmKeyword, setCalmKeyword] = useState(keyword)
    useEffect(() => {
      const timeout = setTimeout(() => setCalmKeyword(keyword), 10)
      return () => clearTimeout(timeout)
    }, [keyword])

    if (calmKeyword === null || calmKeyword === undefined) {
      // Check for undefined too
      return null
    }

    // Show loading indicator while fetching members
    if (shouldShowLoading) {
      return (
        <Animated.View
          entering={FadeInDown}
          exiting={FadeOutDown}
          style={styles.mentionSuggestionContainer}
        >
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="small" />
            <Text style={styles.loadingText}>{t('loadingMembers')}</Text>
          </View>
        </Animated.View>
      )
    }

    const filteredSuggestions = suggestions.filter(one =>
      one.name.toLocaleLowerCase().includes(calmKeyword.toLocaleLowerCase())
    )

    if (filteredSuggestions.length === 0) {
      return (
        <Animated.View
          entering={FadeInDown}
          exiting={FadeOutDown}
          style={styles.mentionSuggestionContainer}
        >
          <View style={styles.statusContainer}>
            <Text style={styles.statusText}>{t('noMembersFound')}</Text>
          </View>
        </Animated.View>
      )
    }

    return (
      <Animated.View
        entering={FadeInDown}
        exiting={FadeOutDown}
        style={styles.mentionSuggestionContainer}
      >
        <FlatList
          data={filteredSuggestions}
          renderItem={({ item }) => (
            <TouchableOpacity
              key={item.id}
              onPress={() => onSuggestionPress(item)}
              style={styles.mentionSuggestionItem}
            >
              <UserAvatar
                user={{ id: item.id, photoUrl: item.photoUrl }}
                showAvatarIfNoPhone
                size={'small'}
              />
              <Text style={{ marginLeft: 10 }}>{item.name}</Text>
            </TouchableOpacity>
          )}
          keyExtractor={item => item.id}
          style={{ maxHeight: 250 }}
          scrollEnabled={true}
          showsVerticalScrollIndicator={true}
          keyboardShouldPersistTaps="always"
        />
      </Animated.View>
    )
  }

const useStyles = makeStyles((theme, selectedAttachments: boolean) => ({
  textInput: {
    color: theme.colors.black,
    marginBottom: Platform.OS === 'ios' ? 6 : 0,
    marginLeft: 10,
    fontSize: 16,
  },
  sendContainer: {
    position: 'relative',
    marginBottom: 3,
  },
  attachmentPreviewRowContainer: {
    position: 'absolute',
    height: ATTACHMENT_PREVIEW_SIZE + COMPOSER_HEIGHT,
    top: -(ATTACHMENT_PREVIEW_SIZE + COMPOSER_HEIGHT),
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey10 : theme.colors.white,
  },
  inputToolbarContainer: {
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey11 : theme.colors.grey6,
    borderTopWidth: 0.5,
    borderTopColor: theme.colors.grey7,
  },
  inputToolbar: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: theme.colors?.grey7,
  },
  divider: {
    color: theme.mode === 'dark' ? theme.colors.grey7 : theme.colors.grey7,
  },
  mentionSuggestionContainer: {
    position: 'absolute',
    width: '100%',
    bottom: '100%',
    marginBottom: 0,
    backgroundColor: theme.colors.white,
  },
  mentionSuggestionItem: {
    padding: 12,
    flexDirection: 'row',
    alignItems: 'center',
  },
  textInputContainer: {
    flex: 1,
    maxHeight: 150,
    ...Platform.select({
      web: {
        paddingTop: 6,
        paddingLeft: 4,
      },
    }),
    marginTop: Platform.select({
      ios: 9,
      android: 0,
      web: 6,
    }),
    marginBottom: Platform.select({
      ios: 8,
      android: 3,
      web: 4,
    }),
  },
  mentionText: {
    color: theme.mode === 'dark' ? '#27BE64' : '#1E8555',
    fontWeight: 'bold',
  },
  replyMessageBar: {
    position: 'absolute',
    height: REPLY_MESSAGE_BAR_HEIGHT + COMPOSER_HEIGHT,
    top: -(
      REPLY_MESSAGE_BAR_HEIGHT +
      COMPOSER_HEIGHT +
      (selectedAttachments ? ATTACHMENT_PREVIEW_SIZE : 0)
    ),
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey10 : theme.colors.white,
  },
  placeholderTextColor: {
    color: theme.colors.grey3,
  },
  loadingContainer: {
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 60,
  },
  loadingText: {
    marginLeft: 12,
    fontSize: 14,
    color: theme.colors.grey5,
  },
  statusContainer: {
    minHeight: 60,
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
  statusText: {
    fontSize: 14,
    color: theme.colors.grey4,
    textAlign: 'center',
  },
}))
